import { useState, type FormEvent } from "react";
import { Button, Modal, TextArea, TextField } from "../../components/ui/index.ts";
import { roleLabel, type Role } from "./types.ts";
import { showError } from "../../utils/toast.ts";
import styles from "./role-management.module.css";

interface RoleFormModalProps {
  role: Role | null;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (values: { displayName: string; description: string }) => Promise<void>;
}

export function RoleFormModal({ role, submitting, onClose, onSubmit }: RoleFormModalProps) {
  const [title, setTitle] = useState(role ? roleLabel(role) : "");
  const [description, setDescription] = useState(role?.description ?? "");

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    if (!title.trim() || /^\d/.test(title.trim()) || !description.trim()) {
      showError("Enter a role title that does not start with a number and a description.");
      return;
    }
    if (title.trim().length > 20 || description.trim().length > 160) {
      showError("Use at most 20 characters for the role title and 160 for the description.");
      return;
    }
    void onSubmit({ displayName: title.trim(), description: description.trim() });
  };

  return (
    <Modal open onClose={() => { if (!submitting) onClose(); }} title={role ? "Edit Role" : "Add Role"} size="md">
      <form onSubmit={submit} className={styles.form} noValidate>
        <TextField id="role-title" label="Role Title" value={title} onChange={(event) => setTitle(event.target.value)} required maxLength={20} disabled={submitting} />
        <TextArea id="role-description" label="Description" value={description} onChange={(event) => setDescription(event.target.value)} required maxLength={160} showCharacterCount disabled={submitting} />
        <div className={styles.actions}>
          <Button variant="light-grey" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" variant="accent" disabled={submitting}>{submitting ? "Saving..." : "Save Role"}</Button>
        </div>
      </form>
    </Modal>
  );
}
